import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import PageHeader from "../../../components/PageHeader";
import PageNotFound from "../../404/PageNotFound";
import Button from "../../../components/Button";
import Image from "../../../components/Image";
import ProjectArtwork from "../../../components/ProjectArtwork";
import CallToAction from "../../../components/CallToAction";
import LiveDemo from "../../../components/LiveDemo";
import projects from "../../../_data/projects.json";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: "easeInOut" },
});

const Section = ({ title, items, delay }) =>
  items?.length ? (
    <motion.section className="projectSection" {...fadeUp(delay)}>
      <h2>{title}</h2>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </motion.section>
  ) : null;

/**
 * Represents the ProjectDetails page component.
 * Displays a full case study for a specific project.
 *
 * @component
 */

const ProjectDetails = () => {
  const { projectSlug } = useParams();
  const slug = decodeURIComponent(projectSlug || "").toLowerCase();

  const project = projects.find((p) => p.slug === slug);

  // Old URLs used the lowercased title (e.g. "/portfolio/taqtiq mobile")
  if (!project) {
    const legacy = projects.find((p) => p.aliases?.includes(slug));
    return legacy ? <Navigate to={`/portfolio/${legacy.slug}`} replace /> : <PageNotFound />;
  }

  const index = projects.indexOf(project);
  const next = projects[(index + 1) % projects.length];
  const image = project.image2 || project.image;

  return (
    <>
      <main className="container portfolio">
        <nav className="breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link> <span aria-hidden="true">›</span> <Link to="/portfolio">Portfolio</Link>{" "}
          <span aria-hidden="true">›</span> <span aria-current="page">{project.title}</span>
        </nav>

        <PageHeader title={project.title} description={project.description} />

        <article className="projectDetails">
          <div className="row">
            <div className="col-12 col-xl-5 projectImage">
              {project.demo && project.embed !== false ? (
                <LiveDemo project={project} />
              ) : image ? (
                <Image src={image} alt={`${project.title} — ${project.description}`} fit="contain" height="auto" />
              ) : (
                <ProjectArtwork project={project} large />
              )}
            </div>
            <div className="col-12 col-xl-7 projectBodyContainer">
              {/* Project facts */}
              <motion.dl className="projectFacts" {...fadeUp(0)}>
                <div>
                  <dt>Role</dt>
                  <dd>{project.role}</dd>
                </div>
                <div>
                  <dt>Client</dt>
                  <dd>{project.client}</dd>
                </div>
                {project.period && (
                  <div>
                    <dt>Period</dt>
                    <dd>{project.period}</dd>
                  </div>
                )}
                <div>
                  <dt>Platforms</dt>
                  <dd>{project.platforms.join(", ")}</dd>
                </div>
              </motion.dl>

              <div className="tech">
                {project.technologies.map((technology, i) => (
                  <motion.span
                    key={technology}
                    className="technology"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.05, ease: "easeInOut" }}
                  >
                    {technology}
                  </motion.span>
                ))}
              </div>

              <div className="projectBody">
                <motion.p className="paragraph projectSummary" {...fadeUp(0.1)}>
                  {project.summary}
                </motion.p>
                {project.body
                  .split("\n")
                  .filter(Boolean)
                  .map((paragraph, i) => (
                    <motion.p className="paragraph" key={i} {...fadeUp(0.2 + i * 0.1)}>
                      {paragraph}
                    </motion.p>
                  ))}
              </div>

              <Section title="Key features" items={project.features} delay={0.3} />
              <Section title="What I did" items={project.contributions} delay={0.4} />
              <Section title="Results" items={project.results} delay={0.5} />

              <motion.div className="projectButtons" {...fadeUp(0.5)}>
                {project.links?.map((link) => (
                  <Button key={link.url} name={link.label} href={link.url} />
                ))}
                <Button name="All projects" to="/portfolio" color="var(--hl2-color)" />
              </motion.div>
            </div>
          </div>
        </article>

        <Link className="nextProject" to={`/portfolio/${next.slug}`}>
          <span>Next project</span>
          <strong>{next.title} →</strong>
        </Link>
      </main>
      <CallToAction title="Need something similar? Let's build it together." />
    </>
  );
};

export default ProjectDetails;
